import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Pause, Play, Square, RotateCcw, FileDown, Bug, ChevronDown, ChevronRight, ExternalLink, Sparkles, Loader2 } from 'lucide-react';
import { runsApi, reportsApi, scopePoliciesApi, aiApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RunProgress } from '../components/ui/run-progress';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Badge, SeverityBadge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import { RUN_STATUS_VARIANT } from '../lib/run-status';
import type { FindingRecord, Severity } from '../lib/types';

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);
/** Worst-first — matches Findings.tsx's SEVERITY_ORDER, so grouping reads the same way everywhere. */
const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];
/** A run with more than this many findings starts collapsed — otherwise expanded. */
const COLLAPSE_THRESHOLD = 10;

export function RunDetail() {
  const { id } = useParams<{ id: string }>();
  const runId = id!;
  const { principal } = useAuth();
  const canManage = principal?.role === undefined || can(principal.role, 'run:manage');
  const canGenerate = principal?.role === undefined || can(principal.role, 'report:generate');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // null until the user explicitly toggles it — the default (expanded at <=10 findings, collapsed
  // above that) is then overridden and stays put regardless of the finding count changing under a
  // live-updating run (metricsQuery refetches every 5s).
  const [findingsExpandedOverride, setFindingsExpandedOverride] = useState<boolean | null>(null);

  const runQuery = useQuery({ queryKey: ['run', runId], queryFn: () => runsApi.get(runId), refetchInterval: 3000 });
  const timelineQuery = useQuery({ queryKey: ['run-timeline', runId], queryFn: () => runsApi.timeline(runId), refetchInterval: 5000 });
  const metricsQuery = useQuery({ queryKey: ['run-metrics', runId], queryFn: () => runsApi.metrics(runId), refetchInterval: 5000 });
  const reportStatusQuery = useQuery({ queryKey: ['report-status', runId], queryFn: () => reportsApi.status(runId) });
  const engagementId = runQuery.data?.run.engagementId;
  const scopePoliciesQuery = useQuery({
    queryKey: ['scope-policies', engagementId],
    queryFn: () => scopePoliciesApi.list(engagementId!),
    enabled: Boolean(engagementId),
  });

  // The scope policy's allowlist is the authoritative record of what this run was authorized to
  // test — unlike the engagement name (a free-text label) or asset-group targets (tenant-wide
  // inventory, not run-scoped), this is exactly what was in scope for THIS engagement.
  const targetLabel = useMemo(() => {
    const values = (scopePoliciesQuery.data?.scopePolicies ?? []).flatMap((p) => [...p.policy.allow.domains, ...p.policy.allow.cidrs, ...p.policy.allow.hosts]);
    return values.length > 0 ? values.join(', ') : null;
  }, [scopePoliciesQuery.data]);

  // Already run-scoped (apps/api/src/routes/runs.ts's /metrics handler groups
  // repos.findings.listForRun's own result) — no separate per-run findings fetch needed. Computed
  // here (ahead of this component's early loading/error returns below) since hooks can't follow a
  // conditional return.
  const runFindings: FindingRecord[] = useMemo(() => Object.values(metricsQuery.data?.findingsByZone ?? {}).flat(), [metricsQuery.data]);
  const findingsBySeverity = useMemo(
    () =>
      SEVERITY_ORDER.map((severity) => ({ severity, findings: runFindings.filter((f) => f.severity === severity) })).filter(
        (group) => group.findings.length > 0,
      ),
    [runFindings],
  );
  // For rendering the AI Risk Priority card's ranked list — the API only returns finding ids +
  // rank + rationale (packages/ai/src/risk-priority.ts), not titles.
  const findingsById = useMemo(() => new Map(runFindings.map((f) => [f.id, f])), [runFindings]);

  const invalidateRun = () => {
    queryClient.invalidateQueries({ queryKey: ['run', runId] });
    queryClient.invalidateQueries({ queryKey: ['run-timeline', runId] });
  };

  const pauseMutation = useMutation({
    mutationFn: () => runsApi.pause(runId),
    onSuccess: () => {
      invalidateRun();
      toast({ title: 'Run paused', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not pause', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const resumeMutation = useMutation({
    mutationFn: () => runsApi.resume(runId),
    onSuccess: () => {
      invalidateRun();
      toast({ title: 'Run resumed', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not resume', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const cancelMutation = useMutation({
    mutationFn: () => runsApi.cancel(runId),
    onSuccess: () => {
      invalidateRun();
      toast({ title: 'Run cancelled', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not cancel', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const retestMutation = useMutation({
    mutationFn: () => runsApi.retest(runId),
    onSuccess: (result) => {
      toast({ title: 'Retest started', variant: 'success' });
      navigate(`/runs/${result.run.id}`);
    },
    onError: (err) => toast({ title: 'Could not start retest', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const generateReportMutation = useMutation({
    mutationFn: () => reportsApi.generate(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-status', runId] });
      toast({ title: 'Report generated', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not generate report', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  // Gates every AI feature card below — hidden entirely (never a button that just degrades) when
  // OPENROUTER_API_KEY isn't configured, same posture as FindingDetail.tsx's newer AI features.
  const aiCapabilitiesQuery = useQuery({ queryKey: ['ai-capabilities'], queryFn: () => aiApi.capabilities() });
  const aiEnabled = aiCapabilitiesQuery.data?.enabled ?? false;

  const executiveSummaryMutation = useMutation({
    mutationFn: () => runsApi.executiveSummary(runId),
    onError: (err) => toast({ title: 'Could not generate summary', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const riskPrioritisationMutation = useMutation({
    mutationFn: () => runsApi.riskPrioritisation(runId),
    onError: (err) => toast({ title: 'Could not prioritise findings', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const retestComparisonMutation = useMutation({
    mutationFn: () => runsApi.retestComparison(runId),
    onError: (err) => toast({ title: 'Could not compare with original', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const downloadReport = async (format: 'json' | 'pdf' | 'pdf-branded') => {
    try {
      const blob = await reportsApi.download(runId, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${runId}.${format === 'pdf-branded' ? 'pdf' : format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Could not download report', description: friendlyErrorMessage(err), variant: 'error' });
    }
  };

  if (runQuery.isLoading) return <LoadingState />;
  if (runQuery.isError) return <ErrorState error={runQuery.error} onRetry={() => runQuery.refetch()} />;

  const run = runQuery.data!.run;
  const isTerminal = TERMINAL.has(run.status);
  const events = timelineQuery.data?.events ?? [];
  const metrics = metricsQuery.data;
  const findingsExpanded = findingsExpandedOverride ?? runFindings.length <= COLLAPSE_THRESHOLD;

  return (
    <div>
      <PageHeader
        title={`Run started ${new Date(run.createdAt).toLocaleString()}`}
        description={targetLabel ? `Target: ${targetLabel}` : undefined}
        action={
          <Badge variant={RUN_STATUS_VARIANT[run.status]} className="text-sm capitalize">
            {run.status}
          </Badge>
        }
      />

      <div className="mb-6">
        <RunProgress
          progressPct={run.progressPct}
          currentModule={run.currentModule}
          completedModules={run.completedModules}
          estimatedCompletion={run.estimatedCompletion}
        />
      </div>

      {/* AI Executive Summary — on-demand narrative, see executiveSummaryMutation above. */}
      {aiEnabled ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles size={18} className="text-brand" />
              AI Executive Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">
            {!executiveSummaryMutation.data && !executiveSummaryMutation.isPending && !executiveSummaryMutation.isError ? (
              <div className="space-y-2">
                <p className="text-ink-muted">Generate an AI-written executive summary for this run.</p>
                <Button size="sm" variant="outline" onClick={() => executiveSummaryMutation.mutate()}>
                  <Sparkles size={14} />
                  Generate summary
                </Button>
              </div>
            ) : executiveSummaryMutation.isPending ? (
              <Button size="sm" variant="outline" disabled>
                <Loader2 size={14} className="animate-spin" />
                Generating...
              </Button>
            ) : executiveSummaryMutation.isError || (executiveSummaryMutation.data && 'error' in executiveSummaryMutation.data) ? (
              <div className="space-y-2">
                <p className="text-ink-muted">Could not generate summary. Try again.</p>
                <Button size="sm" variant="outline" onClick={() => executiveSummaryMutation.mutate()}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="whitespace-pre-line text-ink">{executiveSummaryMutation.data!.summary}</p>
                <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card className="mb-6">
        <button
          type="button"
          onClick={() => runFindings.length > 0 && setFindingsExpandedOverride(!findingsExpanded)}
          aria-expanded={findingsExpanded}
          disabled={runFindings.length === 0}
          className="flex w-full items-center justify-between gap-3 p-6 pb-3 text-left disabled:cursor-default"
        >
          <span className="flex items-center gap-2 text-base font-semibold text-ink">
            {runFindings.length > 0 ? findingsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} /> : null}
            Findings
          </span>
          {runFindings.length > 0 ? (
            <span className="text-xs text-ink-muted">
              {runFindings.length} finding{runFindings.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </button>
        <CardContent className="pt-0">
          {!metrics ? (
            <p className="text-sm text-ink-muted">Loading findings…</p>
          ) : runFindings.length === 0 ? (
            <EmptyState icon={<Bug size={28} />} title="No findings for this run" />
          ) : findingsExpanded ? (
            <div className="space-y-4">
              {findingsBySeverity.map((group) => (
                <div key={group.severity}>
                  <div className="mb-2 flex items-center gap-2">
                    <SeverityBadge severity={group.severity} />
                    <span className="text-xs text-ink-muted">
                      {group.findings.length} finding{group.findings.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {group.findings.map((finding) => (
                      <div
                        key={finding.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium text-ink">{finding.title}</p>
                          <p className="truncate text-xs text-ink-muted">{finding.target}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <SeverityBadge severity={finding.severity} />
                          <Button size="sm" variant="outline" asChild>
                            <Link to={`/findings/${finding.id}`}>
                              <ExternalLink size={14} /> View details
                            </Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* AI Risk Priority — on-demand ranking, see riskPrioritisationMutation above. */}
      {aiEnabled ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles size={18} className="text-brand" />
              AI Risk Priority
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">
            {!riskPrioritisationMutation.data && !riskPrioritisationMutation.isPending && !riskPrioritisationMutation.isError ? (
              <div className="space-y-2">
                <p className="text-ink-muted">See findings ranked by actual business risk.</p>
                <Button size="sm" variant="outline" onClick={() => riskPrioritisationMutation.mutate()}>
                  <Sparkles size={14} />
                  Prioritise findings
                </Button>
              </div>
            ) : riskPrioritisationMutation.isPending ? (
              <Button size="sm" variant="outline" disabled>
                <Loader2 size={14} className="animate-spin" />
                Prioritising...
              </Button>
            ) : riskPrioritisationMutation.isError || (riskPrioritisationMutation.data && 'error' in riskPrioritisationMutation.data) ? (
              <div className="space-y-2">
                <p className="text-ink-muted">Could not prioritise findings. Try again.</p>
                <Button size="sm" variant="outline" onClick={() => riskPrioritisationMutation.mutate()}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <ol className="list-decimal space-y-2 pl-5">
                  {riskPrioritisationMutation.data!.ranked.map((r) => (
                    <li key={r.id}>
                      <p className="font-medium text-ink">{findingsById.get(r.id)?.title ?? r.id}</p>
                      <p className="text-xs text-ink-muted">{r.rationale}</p>
                    </li>
                  ))}
                </ol>
                <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* AI Comparison — retest runs only, see retestComparisonMutation above. */}
      {aiEnabled && run.retestOfRunId ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles size={18} className="text-brand" />
              AI Comparison
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-ink">
            {!retestComparisonMutation.data && !retestComparisonMutation.isPending && !retestComparisonMutation.isError ? (
              <div className="space-y-2">
                <p className="text-ink-muted">See how this retest compares to the original scan.</p>
                <Button size="sm" variant="outline" onClick={() => retestComparisonMutation.mutate()}>
                  <Sparkles size={14} />
                  Compare with original
                </Button>
              </div>
            ) : retestComparisonMutation.isPending ? (
              <Button size="sm" variant="outline" disabled>
                <Loader2 size={14} className="animate-spin" />
                Comparing...
              </Button>
            ) : retestComparisonMutation.isError || (retestComparisonMutation.data && 'error' in retestComparisonMutation.data) ? (
              <div className="space-y-2">
                <p className="text-ink-muted">Could not compare with original. Try again.</p>
                <Button size="sm" variant="outline" onClick={() => retestComparisonMutation.mutate()}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-ink">{retestComparisonMutation.data!.summary}</p>
                <p className="text-xs text-ink-faint">Powered by DefendIQ AI</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {canManage ? (
        <div className="mb-6 flex flex-wrap gap-2">
          {run.status === 'paused' ? (
            <Button size="sm" onClick={() => resumeMutation.mutate()}>
              <Play size={14} /> Resume
            </Button>
          ) : !isTerminal ? (
            <ConfirmDialog
              trigger={
                <Button size="sm" variant="outline">
                  <Pause size={14} /> Pause
                </Button>
              }
              title="Pause this run?"
              description="Testing will stop where it is. You can resume from the same point later — nothing is lost."
              confirmLabel="Pause"
              onConfirm={() => pauseMutation.mutateAsync()}
            />
          ) : null}
          {!isTerminal ? (
            <ConfirmDialog
              trigger={
                <Button size="sm" variant="danger">
                  <Square size={14} /> Cancel
                </Button>
              }
              title="Cancel this run?"
              description="This stops testing permanently for this run. Findings already discovered are kept, but no further testing will happen. This cannot be undone."
              confirmLabel="Cancel run"
              variant="danger"
              onConfirm={() => cancelMutation.mutateAsync()}
            />
          ) : null}
          {run.status === 'completed' ? (
            <Button size="sm" variant="outline" onClick={() => retestMutation.mutate()} disabled={retestMutation.isPending}>
              <RotateCcw size={14} /> Start a retest
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p title="Additional subdomains found via DNS enumeration, beyond the target(s) already in scope — 0 is expected for a target with no other discoverable subdomains, not a sign the scan didn't run.">
              <span className="text-ink-muted">Additional subdomains discovered:</span> {metrics?.targetsDiscovered ?? '—'}
            </p>
            <p>
              <span className="text-ink-muted">Findings so far:</span>{' '}
              {metrics ? (
                <Link
                  to={`/findings?engagementId=${run.engagementId}`}
                  className="font-medium text-brand underline-offset-2 hover:underline"
                >
                  {metrics.findingsCount} finding{metrics.findingsCount === 1 ? '' : 's'} →
                </Link>
              ) : (
                '—'
              )}
            </p>
            <p>
              <span className="text-ink-muted">Discovery jobs pending:</span> {run.discoveryPending}
            </p>
            <p>
              <span className="text-ink-muted">Validation jobs pending:</span> {run.validationPending}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Report</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {canGenerate ? (
              <Button size="sm" onClick={() => generateReportMutation.mutate()} disabled={generateReportMutation.isPending}>
                Generate report
              </Button>
            ) : null}
            {reportStatusQuery.data?.available.json || reportStatusQuery.data?.available.pdfBranded ? (
              <div className="flex gap-2">
                {reportStatusQuery.data.available.json ? (
                  <Button size="sm" variant="outline" onClick={() => downloadReport('json')}>
                    <FileDown size={14} /> JSON
                  </Button>
                ) : null}
                {/* The BUI-branded PDF (packages/reports/src/pdf/branded-pdf-report.ts) — generate()
                    always requests it via ?branded=true, so this is the only PDF this page offers. */}
                {reportStatusQuery.data.available.pdfBranded ? (
                  <Button size="sm" variant="outline" onClick={() => downloadReport('pdf-branded')}>
                    <FileDown size={14} /> PDF
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-ink-muted">No report generated yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="max-h-64 space-y-2 overflow-y-auto text-xs">
              {events.length === 0 ? <li className="text-ink-muted">No events yet.</li> : null}
              {events
                .slice()
                .reverse()
                .map((event) => (
                  <li key={event.id} className="border-b border-border pb-2 last:border-none">
                    <p className="font-medium text-ink">{event.kind}</p>
                    <p className="text-ink-faint">{new Date(event.createdAt).toLocaleString()}</p>
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
